import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { X, CheckCircle2, AlertCircle, AlertTriangle, Info } from 'lucide-react';

const ToastContext = createContext(null);

export const useToast = () => {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
};

// Unique ID for toast
let id = 0;
const getUniqueId = () => {
  return `toast-${id++}`;
};

export const ToastProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const timerMapRef = useRef(new Map());

  // Cleanup all timers on unmount
  useEffect(() => {
    return () => {
      timerMapRef.current.forEach((timerId) => clearTimeout(timerId));
      timerMapRef.current.clear();
    };
  }, []);

  const addToast = useCallback((toast) => {
    // #372: be robust to how callers invoke it. A bare string becomes the
    // message; a `message` key is an alias for `description`. Previously a
    // string was spread into char-index keys (or `message` ignored), silently
    // dropping the text at ~27+ call sites.
    const t = typeof toast === 'string' ? { description: toast } : (toast || {});
    const description = t.description ?? t.message;
    if (import.meta.env?.DEV && !t.title && !description) {
      console.warn('[Toast] no title/description/message — the toast text will be blank:', toast);
    }
    const toastId = t.id || getUniqueId();
    const newToast = {
      id: toastId,
      title: t.title || 'Notification',
      description,
      type: t.type || 'success',
      duration: t.duration || 5000,
    };

    setToasts((prevToasts) => [...prevToasts, newToast]);

    // Auto-dismiss toast after duration (tracked for cleanup)
    const timerId = setTimeout(() => {
      removeToast(toastId);
      timerMapRef.current.delete(toastId);
    }, newToast.duration);
    timerMapRef.current.set(toastId, timerId);

    return toastId;
  }, []);

  const removeToast = useCallback((id) => {
    // Clear the auto-dismiss timer if toast is manually removed
    const timerId = timerMapRef.current.get(id);
    if (timerId) {
      clearTimeout(timerId);
      timerMapRef.current.delete(id);
    }
    setToasts((prevToasts) => prevToasts.filter((toast) => toast.id !== id));
  }, []);

  // #372: normalize a bare-string arg to an object BEFORE spreading — otherwise
  // `{ ...'oops' }` yields char-index keys and the message is lost.
  const asObj = (props) => (typeof props === 'string' ? { description: props } : (props || {}));

  const toast = useCallback(
    (props) => {
      return addToast({ ...asObj(props), type: 'success' });
    },
    [addToast]
  );

  toast.success = useCallback(
    (props) => {
      return addToast({ ...asObj(props), type: 'success' });
    },
    [addToast]
  );

  toast.error = useCallback(
    (props) => {
      return addToast({ ...asObj(props), type: 'error' });
    },
    [addToast]
  );

  toast.warning = useCallback(
    (props) => {
      return addToast({ ...asObj(props), type: 'warning' });
    },
    [addToast]
  );

  toast.info = useCallback(
    (props) => {
      return addToast({ ...asObj(props), type: 'info' });
    },
    [addToast]
  );

  // Backward compatibility helper for showToast(message, type)
  const showToast = useCallback(
    (message, type = 'success') => {
      return addToast({
        title: type === 'error' ? 'Error' : type === 'warning' ? 'Warning' : type === 'info' ? 'Info' : 'Success',
        description: message,
        type: type,
      });
    },
    [addToast]
  );

  const value = {
    toast,
    toasts,
    removeToast,
    showToast,
  };

  const getToastIcon = (type) => {
    switch (type) {
      case 'success':
        return <CheckCircle2 size={18} className="text-green-500 flex-shrink-0" />;
      case 'error':
        return <AlertCircle size={18} className="text-red-500 flex-shrink-0" />;
      case 'warning':
        return <AlertTriangle size={18} className="text-amber-500 flex-shrink-0" />;
      case 'info':
        return <Info size={18} className="text-blue-500 flex-shrink-0" />;
      default:
        return <CheckCircle2 size={18} className="text-gray-500 flex-shrink-0" />;
    }
  };

  return (
    <ToastContext.Provider value={value}>
      {children}
      <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-sm w-full">
        {toasts.map((t) => (
          <div
            key={t.id}
            className="bg-white border border-gray-200 rounded-lg shadow-lg px-4 py-3 transform transition-all duration-300 ease-out animate-[slideIn_0.3s_ease-out]"
            role="alert"
          >
            <div className="flex items-start gap-3">
              {getToastIcon(t.type)}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">{t.title}</p>
                {t.description && (
                  <p className="text-sm text-gray-500 mt-0.5">{t.description}</p>
                )}
              </div>
              <button
                onClick={() => removeToast(t.id)}
                className="text-gray-400 hover:text-gray-600 transition-colors flex-shrink-0"
                aria-label="Close"
              >
                <X size={16} />
              </button>
            </div>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}; 